import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { Play, Pause, SkipBack, SkipForward } from "lucide-react";
import { SectionHead } from "./Section";
import { BrowserFrame, PhoneFrame } from "./Frames";
import { TUTORIALS } from "./homeData";
import { DepotScreens } from "./screens/DepotScreens";
import { RosterScreens } from "./screens/RosterScreens";
import { PhoneScreens } from "./screens/PhoneScreens";
import { PayrollScreens } from "./screens/PayrollScreens";

const SCREENS = { depots: DepotScreens, roster: RosterScreens, clock: PhoneScreens, payroll: PayrollScreens };
const STEP_MS = 6000;

const Picker = ({ t, pick }) => (
  <div className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0 -mx-5 px-5 lg:mx-0 lg:px-0" data-testid="tutorial-picker">
    {TUTORIALS.map((x, i) => (
      <button key={x.id} onClick={() => pick(i)} data-testid={`tutorial-tab-${x.id}`}
        className={`shrink-0 w-[230px] lg:w-full text-left rounded-xl border p-4 transition-colors relative overflow-hidden ${i === t ? "bg-white/10 border-[#EA580C]/70" : "border-white/10 hover:bg-white/5"}`}>
        {i === t && <motion.span layoutId="tut-bar" className="absolute left-0 inset-y-0 w-1 bg-[#EA580C]" />}
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-[#EA580C] font-bold">0{i + 1}</span>
          <x.icon className="w-4 h-4 text-slate-300" />
          <span className="font-display font-bold text-white">{x.title}</span>
        </div>
        <p className="mt-2 text-sm text-slate-400 hidden lg:block">{x.blurb}</p>
        <p className="mt-2 font-mono text-[11px] text-slate-500">{x.steps.length} STEPS · {x.device === "phone" ? "PHONE" : "DASHBOARD"}</p>
      </button>
    ))}
  </div>
);

const Ctrl = ({ onClick, label, children, testid, primary }) => (
  <button onClick={onClick} aria-label={label} data-testid={testid}
    className={`w-11 h-11 rounded-full grid place-items-center transition-colors ${primary ? "bg-[#EA580C] text-white hover:bg-[#C2410C]" : "border border-white/15 text-white hover:bg-white hover:text-[#0B1120]"}`}>{children}</button>
);

export const Walkthroughs = () => {
  const [t, setT] = useState(0);
  const [s, setS] = useState(0);
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.35, once: true });
  const tut = TUTORIALS[t];
  const Screen = SCREENS[tut.id];

  useEffect(() => { if (inView) setPlaying(true); }, [inView]);
  const next = useCallback(() => {
    if (s < tut.steps.length - 1) setS(s + 1);
    else { setT((t + 1) % TUTORIALS.length); setS(0); }
  }, [s, t, tut.steps.length]);
  const prev = () => {
    if (s > 0) setS(s - 1);
    else if (t > 0) { setT(t - 1); setS(TUTORIALS[t - 1].steps.length - 1); }
  };
  useEffect(() => {
    if (!playing) return undefined;
    const id = setTimeout(next, STEP_MS);
    return () => clearTimeout(id);
  }, [playing, next]);
  const pick = (i) => { setT(i); setS(0); setPlaying(true); };
  const step = tut.steps[s];

  return (
    <section id="walkthroughs" ref={ref} className="scroll-mt-16 bg-[#0B1120] text-white py-24 lg:py-32 home-noise" data-testid="home-walkthroughs">
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead dark num="05" eyebrow="Walkthroughs" title="See it work, step by step."
          sub="Pick a walkthrough and watch it play, or step through at your own pace." />
        <div className="mt-14 grid lg:grid-cols-[320px_1fr] gap-8 lg:gap-12">
          <Picker t={t} pick={pick} />
          <div>
            <div className="rounded-2xl border border-white/10 bg-[#111a2e] home-grid p-4 sm:p-8 min-h-[470px] sm:min-h-[660px] flex items-center justify-center" data-testid="tutorial-stage">
              <AnimatePresence mode="wait">
                <motion.div key={`${t}-${s}`} initial={{ opacity: 0, y: 14, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }} className={tut.device === "phone" ? "w-[200px] sm:w-[260px]" : "w-full"}>
                  {tut.device === "phone"
                    ? <PhoneFrame testid="tutorial-phone"><Screen step={s} /></PhoneFrame>
                    : <BrowserFrame testid="tutorial-browser"><Screen step={s} /></BrowserFrame>}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="mt-6 grid md:grid-cols-[1fr_auto] gap-6 items-end">
              <AnimatePresence mode="wait">
                <motion.div key={`${t}-${s}`} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} data-testid="tutorial-caption">
                  <p className="font-mono text-xs font-bold text-[#EA580C]" data-testid="tutorial-step-count">STEP 0{s + 1} / 0{tut.steps.length}</p>
                  <h3 className="font-display mt-2 text-xl sm:text-2xl font-bold tracking-tight" data-testid="tutorial-step-title">{step.title}</h3>
                  <p className="mt-2 text-slate-400 max-w-2xl">{step.caption}</p>
                </motion.div>
              </AnimatePresence>
              <div className="flex items-center gap-2">
                <Ctrl onClick={prev} label="Previous step" testid="tutorial-prev-btn"><SkipBack className="w-4 h-4" /></Ctrl>
                <Ctrl onClick={() => setPlaying((p) => !p)} label={playing ? "Pause" : "Play"} testid="tutorial-play-btn" primary>
                  {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                </Ctrl>
                <Ctrl onClick={next} label="Next step" testid="tutorial-next-btn"><SkipForward className="w-4 h-4" /></Ctrl>
              </div>
            </div>
            <div className="mt-6 flex gap-2" data-testid="tutorial-progress">
              {tut.steps.map((x, i) => (
                <button key={x.title} onClick={() => setS(i)} aria-label={`Step ${i + 1}`} data-testid={`tutorial-step-${i}`} className="flex-1 h-6 flex items-center">
                  <span className="relative w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
                    {i < s && <span className="absolute inset-0 bg-[#EA580C]" />}
                    {i === s && (
                      <motion.span key={`${t}-${s}-${playing}`} className="absolute inset-y-0 left-0 bg-[#EA580C]"
                        initial={{ width: playing ? "0%" : "100%" }} animate={{ width: "100%" }} transition={{ duration: playing ? STEP_MS / 1000 : 0, ease: "linear" }} />
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
